import { useState, type KeyboardEvent } from 'react';
import { configApiRef, useApi } from '@backstage/frontend-plugin-api';
import {
  DEFAULT_JIRA_PROJECT,
  normalizeTicketKey,
} from '@internal/backstage-plugin-delivery-common';
import InputAdornment from '@material-ui/core/InputAdornment';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const DEFAULT_BROWSE_URL = 'https://isagri.atlassian.net/browse/';

/** Builds the Jira URL of a ticket key (`delivery.jira.browseUrl`). */
export function useJiraLink(): (key: string) => string {
  const configured = useApi(configApiRef).getOptionalString(
    'delivery.jira.browseUrl',
  );
  const base = configured ?? DEFAULT_BROWSE_URL;
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return (key: string) => `${prefix}${key}`;
}

/** Ticket key linking to Jira; `heading` for page headers, `inline` for table cells. */
export function TicketLink({
  ticketKey,
  variant = 'inline',
}: {
  ticketKey: string;
  variant?: 'heading' | 'inline';
}) {
  const href = useJiraLink()(ticketKey);
  if (variant === 'heading') {
    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'inherit',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {ticketKey}
        <OpenInNewIcon style={{ fontSize: 14, opacity: 0.5 }} />
      </Link>
    );
  }
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      style={{ color: 'inherit', fontWeight: 'inherit' }}
    >
      {ticketKey}
    </Link>
  );
}

/** Project of a bare ticket number: the first of `delivery.jiraProjects`. */
export function useJiraProject(): string {
  const projects = useApi(configApiRef).getOptionalStringArray(
    'delivery.jiraProjects',
  );
  return projects?.[0] ?? DEFAULT_JIRA_PROJECT;
}

/** The text shown for a key of `project`: its number alone, next to the `MP-` prefix. */
export function ticketInputText(raw: string, project: string): string {
  const key = normalizeTicketKey(raw, project);
  const prefix = `${project.toUpperCase()}-`;
  return key?.startsWith(prefix) ? key.slice(prefix.length) : raw;
}

/**
 * Jira key input with a fixed `MP-` prefix: the number alone is enough, and a pasted key, Jira
 * URL or branch is reduced to its number. `value` is the raw text; read the key with
 * `normalizeTicketKey(value, project)`. The format error waits for blur or Enter.
 */
export function TicketKeyField({
  value,
  onChange,
  project,
  label = 'Clé du ticket',
  helperText,
  isValid = key => key !== null,
  invalidText = `Format attendu : ${project}-1234`,
  onEnter,
  id = 'ticket-key',
  size,
  outlined,
  fullWidth,
  disabled,
}: {
  value: string;
  onChange: (raw: string) => void;
  project: string;
  label?: string;
  /** Shown while the key is valid. */
  helperText?: string;
  /** Extra check on the normalised key, e.g. one project only. */
  isValid?: (key: string | null) => boolean;
  invalidText?: string;
  onEnter?: () => void;
  id?: string;
  size?: 'small' | 'medium';
  outlined?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
}) {
  const [touched, setTouched] = useState(false);
  const text = ticketInputText(value, project);
  const error =
    touched &&
    Boolean(value.trim()) &&
    !isValid(normalizeTicketKey(value, project));
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    setTouched(true);
    onEnter?.();
  };
  return (
    <TextField
      id={id}
      label={label}
      placeholder="1234"
      size={size}
      {...(outlined ? { variant: 'outlined' as const } : {})}
      fullWidth={fullWidth}
      disabled={disabled}
      value={text}
      error={error}
      helperText={error ? invalidText : helperText}
      onChange={e => {
        setTouched(false);
        onChange(e.target.value);
      }}
      onBlur={() => setTouched(true)}
      onKeyDown={onKeyDown}
      InputProps={{
        startAdornment: /^\d*$/.test(text) ? (
          <InputAdornment position="start">{`${project.toUpperCase()}-`}</InputAdornment>
        ) : undefined,
      }}
      inputProps={{ inputMode: 'text', autoComplete: 'off', spellCheck: false }}
    />
  );
}
